import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { BarChart3, Users, MessageSquare, FileText, Bot, TrendingUp, TrendingDown, Clock, Award } from "lucide-react";

const COLORS = ["#6366f1","#8b5cf6","#ec4899","#14b8a6","#f59e0b","#ef4444","#3b82f6","#10b981"];

function StatCard({ label, value, trend, trendLabel, icon: Icon, color }) {
  const up = trend > 0;
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-white/10 rounded-2xl p-5 flex flex-col gap-3" data-testid={`stat-${label.toLowerCase().replace(/ /g,"-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">{label}</span>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background: color + '22'}}>
          <Icon className="w-4 h-4" style={{color}} />
        </div>
      </div>
      <p className="text-3xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {trend !== undefined && trend !== null && (
        <div className="flex items-center gap-1 text-xs">
          {up ? <TrendingUp className="w-3 h-3 text-emerald-400" /> : <TrendingDown className="w-3 h-3 text-red-400" />}
          <span className={up ? "text-emerald-400" : "text-red-400"}>{Math.abs(trend).toLocaleString()}</span>
          <span className="text-muted-foreground">{trendLabel || "this week"}</span>
        </div>
      )}
    </div>
  );
}

function LineChart({ data, color = "#6366f1", height = 180 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 100, h = 100;
  // Avoid divide-by-zero when there is only a single data point — center it.
  const denom = data.length === 1 ? 1 : data.length - 1;
  const points = data.map((d, i) => `${(i / denom) * w},${h - (d.count / max) * h}`).join(" ");
  const fillPoints = `0,${h} ${points} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h + 10}`} className="w-full" style={{height}} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`lg-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill={`url(#lg-${color.slice(1)})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {data.length > 0 && data.length <= 14 && data.map((d, i) => (
        <circle key={i} cx={(i / denom) * w} cy={h - (d.count / max) * h} r="1.5" fill={color} />
      ))}
    </svg>
  );
}

function DonutChart({ data, height = 200 }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  let cumulative = 0;
  const r = 40, cx = 50, cy = 50;
  const circ = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="flex-shrink-0" style={{width: height, height}}>
        {data.map((d, i) => {
          const pct = d.count / total;
          const offset = circ * cumulative;
          cumulative += pct;
          return <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="12" strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`} strokeDashoffset={-offset} transform={`rotate(-90 ${cx} ${cy})`} />;
        })}
        <text x={cx} y={cy} textAnchor="middle" dy="0.35em" fontSize="12" fill="white" fontWeight="bold">{total}</text>
      </svg>
      <div className="flex flex-col gap-1.5 min-w-0">
        {data.slice(0, 8).map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{background: COLORS[i % COLORS.length]}} />
            <span className="text-muted-foreground/60 truncate">{d.model || d.type || d.purpose}</span>
            <span className="text-muted-foreground ml-auto">{d.percentage !== undefined ? `${d.percentage}%` : d.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChartH({ data, height = 200 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.message_count || d.count || 0), 1);
  return (
    <div className="flex items-end gap-[3px]" style={{height}}>
      {data.map((d, i) => {
        const val = d.message_count || d.count || 0;
        const h = Math.max(4, (val / max) * 100);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-t" style={{height: `${h}%`, background: COLORS[i % 4], minHeight: 4, transition: 'height 0.5s'}} title={`${d.hour !== undefined ? d.hour + ':00' : d.type} — ${val}`} />
            {data.length <= 24 && <span className="text-[8px] text-muted-foreground">{d.hour !== undefined ? d.hour : ''}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [content, setContent] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ov, en, ai, co, my] = await Promise.all([
        api.get("/analytics/overview").catch(() => ({ data: null })),
        api.get("/analytics/engagement").catch(() => ({ data: null })),
        api.get("/analytics/ai-usage").catch(() => ({ data: null })),
        api.get("/analytics/content").catch(() => ({ data: null })),
        api.get("/analytics/my-stats").catch(() => ({ data: null })),
      ]);
      setOverview(ov.data);
      setEngagement(en.data);
      setAiUsage(ai.data);
      setContent(co.data);
      setMyStats(my.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user) { navigate("/login"); return null; }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen text-white" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-300 to-violet-400 bg-clip-text text-transparent">Analytics</h1>
            <p className="text-sm text-muted-foreground/70 mt-1">{isAdmin ? "Platform Overview" : "Your Activity"}</p>
          </div>
          <button onClick={() => navigate("/feed")} className="text-xs text-muted-foreground/70 hover:text-white bg-white/5 border border-white/10 rounded-lg px-3 py-2 cursor-pointer transition-colors" data-testid="back-to-feed">Back to Feed</button>
        </div>

        {/* Stat Cards: platform-wide totals are admin-only. Non-admins see
            the same row populated from /analytics/my-stats so the labels
            and numbers are consistent with each other. */}
        {isAdmin && overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stat-cards">
            <StatCard label="Total Messages" value={overview.total_messages} trend={overview.messages_this_week} trendLabel="this week" icon={MessageSquare} color="#6366f1" />
            <StatCard label="AI Companions" value={overview.total_assistants} icon={Bot} color="#8b5cf6" />
            <StatCard label="Posts Created" value={overview.total_posts} trend={overview.posts_today} trendLabel="today" icon={FileText} color="#ec4899" />
            <StatCard label="Users" value={overview.total_users} trend={overview.new_users_this_week} trendLabel="new this week" icon={Users} color="#14b8a6" />
          </div>
        )}
        {!isAdmin && myStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stat-cards">
            <StatCard label="My Messages" value={myStats.my_total_messages || 0} icon={MessageSquare} color="#6366f1" />
            <StatCard label="My AI Companions" value={myStats.my_assistants_count || 0} icon={Bot} color="#8b5cf6" />
            <StatCard label="My Posts" value={myStats.my_posts_count || 0} icon={FileText} color="#ec4899" />
            <StatCard label="My Followers" value={myStats.my_followers || 0} icon={Users} color="#14b8a6" />
          </div>
        )}

        {/* Charts Grid (admin-only platform metrics) */}
        {isAdmin && <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Activity Over Time */}
          {engagement?.messages_per_day && (
            <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-5" data-testid="chart-activity">
              <h3 className="text-sm font-semibold text-white mb-1">Activity Over Time</h3>
              <p className="text-xs text-muted-foreground mb-4">Messages per day (30 days)</p>
              <LineChart data={engagement.messages_per_day} color="#6366f1" />
              <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                <span>{engagement.messages_per_day[0]?.date}</span>
                <span>{engagement.messages_per_day[engagement.messages_per_day.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {/* AI Model Usage */}
          {aiUsage?.messages_by_model && (
            <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-5" data-testid="chart-ai-usage">
              <h3 className="text-sm font-semibold text-white mb-1">AI Model Usage</h3>
              <p className="text-xs text-muted-foreground mb-4">Distribution across models</p>
              <DonutChart data={aiUsage.messages_by_model} height={160} />
            </div>
          )}

          {/* Most Active Hours */}
          {engagement?.most_active_hours && (
            <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-5" data-testid="chart-hours">
              <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-400" /> Most Active Hours</h3>
              <p className="text-xs text-muted-foreground mb-4">Message volume by hour</p>
              <BarChartH data={engagement.most_active_hours} height={120} />
            </div>
          )}

          {/* Content Breakdown */}
          {content?.posts_by_type && content.posts_by_type.length > 0 && (
            <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-5" data-testid="chart-content">
              <h3 className="text-sm font-semibold text-white mb-1">Content Breakdown</h3>
              <p className="text-xs text-muted-foreground mb-4">Posts by type</p>
              <div className="space-y-3">
                {content.posts_by_type.map((d, i) => {
                  const max = content.posts_by_type[0]?.count || 1;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground/60 w-20 truncate">{d.type}</span>
                      <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{width: `${(d.count / max) * 100}%`, background: COLORS[i % COLORS.length]}} />
                      </div>
                      <span className="text-xs text-muted-foreground/70 w-8 text-right">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>}

        {/* Top Assistants Table (admin only) */}
        {isAdmin && aiUsage?.top_assistants && aiUsage.top_assistants.length > 0 && (
          <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-5 mb-8" data-testid="table-top-assistants">
            <h3 className="text-sm font-semibold text-white mb-4">Top AI Companions</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-muted-foreground/70 border-b border-white/10">
                  <th className="text-left py-2 px-2">#</th><th className="text-left py-2 px-2">Name</th><th className="text-left py-2 px-2">Purpose</th><th className="text-left py-2 px-2">Model</th><th className="text-right py-2 px-2">Conversations</th>
                </tr></thead>
                <tbody>
                  {aiUsage.top_assistants.map((a, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-2.5 px-2 text-muted-foreground">{i + 1}</td>
                      <td className="py-2.5 px-2 text-white font-medium">{a.name}</td>
                      <td className="py-2.5 px-2 text-muted-foreground/70">{a.purpose?.replace(/_/g, " ")}</td>
                      <td className="py-2.5 px-2"><span className="px-2 py-0.5 rounded bg-indigo-500/20 text-cyan-300">{a.model}</span></td>
                      <td className="py-2.5 px-2 text-right text-muted-foreground/60">{a.message_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Personal Stats */}
        {myStats && (
          <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-cyan-400/20 rounded-2xl p-6" data-testid="personal-stats">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-cyan-400" /> Your Activity</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="text-center"><p className="text-2xl font-bold text-white">{myStats.my_total_messages}</p><p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">Messages</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{myStats.my_posts_count}</p><p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">Posts</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{myStats.my_streak}</p><p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">Streak</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{myStats.my_achievements_count}</p><p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">Achievements</p></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground/70 mb-2">Followers: {myStats.my_followers} / Following: {myStats.my_following}</p>
                {myStats.my_most_active_ai && (
                  <p className="text-xs text-muted-foreground/70">Most active AI: <span className="text-cyan-300 font-medium">{myStats.my_most_active_ai.name}</span></p>
                )}
              </div>
              {myStats.messages_by_day && (
                <div>
                  <p className="text-xs text-muted-foreground/70 mb-2">Your messages (14 days)</p>
                  <LineChart data={myStats.messages_by_day} color="#8b5cf6" height={80} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
